<template>
  <div class="memorial-dashboard-page">
    <!-- Band 1: Header (beige) -->
    <div class="band band-hero">
      <div class="band-content">
        <div class="page-header">
          <div class="brand-header">
            <router-link to="/">
              <PassingOnLogo class="brand-logo-svg" :name2-override="memorial?.nameOfPerson ? '-' + memorial.nameOfPerson : null" />
            </router-link>
          </div>
          <ProfileMenu />
        </div>
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
    <v-alert v-if="error" type="error" variant="tonal" class="ma-4">
      {{ error }}
    </v-alert>

    <template v-if="!loading && memorial">
      <!-- Band 2: Dark (photo + details) -->
      <div class="band band-details">
        <div class="band-content">
          <div class="memorial-hero">
          <div class="hero-layout">
            <!-- Profile Photo -->
            <div class="hero-photo-container">
              <img
                v-if="memorial.profilePhoto"
                :src="memorial.profilePhoto.location ? `${memorial.profilePhoto.location}/${memorial.profilePhoto.filename}.${memorial.profilePhoto.extension}` : `https://assets.forever-x.com/photos/${memorial.profilePhoto.filename}.${memorial.profilePhoto.extension}`"
                :alt="memorial.nameOfPerson"
                class="hero-photo"
              />
              <div v-else class="hero-photo-placeholder">
                <v-icon size="120" color="grey-lighten-1">mdi-account</v-icon>
              </div>
            </div>

            <div class="hero-info">
              <div v-if="memorial.birthDate || memorial.deathDate || (hasEvent && (memorial.eventName || memorial.memorialDate || memorial.address || memorial.details))" class="hero-details-panel">
                <h1 v-if="memorial.birthDate && memorial.deathDate" class="life-heading">
                  {{ memorial.nameOfPerson || 'Memory' }}
                  <span>{{ formatLifeDate(memorial.birthDate) }} - {{ formatLifeDate(memorial.deathDate) }}</span>
                </h1>

                <div v-if="memorial.birthDate && !memorial.deathDate" class="hero-detail-item">
                  <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-cake-variant-outline</v-icon>
                  <span>Born {{ formatLifeDate(memorial.birthDate) }}</span>
                </div>

                <div v-if="memorial.deathDate && !memorial.birthDate" class="hero-detail-item">
                  <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-flower-outline</v-icon>
                  <span>Died {{ formatLifeDate(memorial.deathDate) }}</span>
                </div>

                <div v-if="hasEvent && memorial.eventName" class="hero-detail-item">
                  <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-calendar-star</v-icon>
                  <span>{{ memorial.eventName }}</span>
                </div>

                <div v-if="hasEvent && memorial.memorialDate" class="hero-detail-item">
                  <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-calendar</v-icon>
                  <span>Event: {{ formatMemorialDate(memorial.memorialDate) }}<template v-if="memorial.memorialHour != null">&nbsp;&nbsp;{{ String(memorial.memorialHour).padStart(2, '0') }}:{{ String(memorial.memorialMinute ?? 0).padStart(2, '0') }}</template></span>
                </div>

                <div v-if="hasEvent && memorial.address" class="hero-detail-row">
                  <div class="hero-detail-item address-detail">
                    <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-map-marker</v-icon>
                    <span>{{ memorial.address }}</span>
                  </div>
                  <img
                    src="/images/map-icon.png"
                    alt=""
                    class="map-mini-icon"
                    role="button"
                    tabindex="0"
                    @click="toggleMap"
                    @keydown.enter.prevent="toggleMap"
                    @keydown.space.prevent="toggleMap"
                  />
                </div>

                <div v-if="hasEvent && memorial.details" class="hero-detail-item hero-details-text">
                  <span>{{ memorial.details }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="hasEvent && memorial.address && memorial.address.trim() && mapExpanded" class="hero-map mt-6">
            <iframe
              :src="mapSrc"
              width="100%"
              height="250"
              style="border:0; border-radius: 8px;"
              allowfullscreen
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          </div>
        </div>
      </div>

      <!-- Red stripe -->
      <div class="band-red-stripe"></div>

      <!-- Band 3: White (action cards) -->
      <div class="band band-main">
        <div class="band-content">

        <!-- Memorial Actions Section -->
        <v-card class="section-card mb-6" elevation="0">
          <v-card-title class="section-header primary-header">
            <v-icon left color="primary">mdi-pencil</v-icon>
            Edit Memory
          </v-card-title>
          <v-card-text class="pa-6">
            <div class="action-buttons">
              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-pencil"
                class="mr-2 mb-2"
                @click="editDetails"
              >
                Edit Details
              </v-btn>

              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-message-text"
                class="mr-2 mb-2"
                @click="editMessage"
              >
                Edit Message
              </v-btn>

              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-image-multiple"
                class="mr-2 mb-2"
                @click="editPhotos"
              >
                Edit My Photos
              </v-btn>

              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-book-open-page-variant"
                class="mr-2 mb-2"
                @click="editStories"
              >
                Edit Stories
              </v-btn>

              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-account-multiple"
                class="mr-2 mb-2"
                @click="editContactsAndShare"
              >
                Edit Contacts and Share
              </v-btn>

              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-shield-account"
                class="mr-2 mb-2"
                @click="editAdministrators"
              >
                Administrators
              </v-btn>

              <v-btn
                v-if="!memorial?.dateDeleteRequested"
                color="warning"
                variant="outlined"
                prepend-icon="mdi-delete-outline"
                class="mr-2 mb-2"
                @click="showDeleteDialog = true"
              >
                Request Delete
              </v-btn>

              <v-btn
                v-else
                color="success"
                variant="outlined"
                prepend-icon="mdi-cancel"
                class="mr-2 mb-2"
                @click="showCancelDeleteDialog = true"
              >
                Cancel Delete Request
              </v-btn>

              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-currency-usd"
                class="mb-2"
                disabled
                title="Donation editing is temporarily unavailable"
              >
                Edit Donations
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- Publish Section -->
        <v-card class="section-card mb-6" elevation="0">
          <v-card-title class="section-header primary-header">
            <v-icon left color="primary">mdi-publish</v-icon>
            Visualise
          </v-card-title>
          <v-card-text class="pa-6">
            <p class="text-body-1 mb-4">
              When you are happy with your information and the contributions, you can create a PDF document that has all the information in a presentable format, and if you want a slide show for the memory click on the appropriate button below.
            </p>

            <div class="action-buttons">
              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-view-stream"
                class="mr-2 mb-2"
                @click="viewFeed"
              >
                Feed
              </v-btn>

              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-presentation-play"
                class="mr-2 mb-2"
                @click="viewSlideShow"
              >
                View Slide Show
              </v-btn>

              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-video-box"
                class="mr-2 mb-2"
                @click="viewVideoSlideShow"
              >
                Video Slide Show
              </v-btn>

              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-file-pdf-box"
                class="mb-2"
                :loading="generatingPdf"
                @click="exportToPdf"
              >
                Export to PDF
              </v-btn>
            </div>

            <!-- PDF Link Display -->
            <v-alert
              v-if="memorial?.pdfUrl"
              type="success"
              variant="tonal"
              class="mt-4"
              border="start"
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="font-weight-bold mb-1">PDF Generated</div>
                  <div class="text-caption">
                    Created: {{ formatDate(memorial.pdfGeneratedAt) }}
                  </div>
                </div>
                <v-btn
                  :href="memorial.pdfUrl"
                  target="_blank"
                  color="success"
                  variant="outlined"
                  prepend-icon="mdi-download"
                  size="small"
                  style="background: white;"
                >
                  Download PDF
                </v-btn>
              </div>
            </v-alert>
          </v-card-text>
        </v-card>

        <v-dialog v-model="showDeleteDialog" max-width="520">
          <v-card>
            <v-card-title class="text-h6">Delete memory?</v-card-title>
            <v-card-text>
              <p>
                You can permanently delete this memory and all information, to do so, press the request delete button below and your memory will be scheduled for deletion in 7 days.
              </p>
              <v-alert v-if="deleteRequestError" type="error" variant="tonal" class="mt-4">
                {{ deleteRequestError }}
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="showDeleteDialog = false" :disabled="deleteRequestLoading">Cancel</v-btn>
              <v-btn
                color="warning"
                :loading="deleteRequestLoading"
                :disabled="deleteRequestLoading"
                @click="requestDeletion"
              >
                Request delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="showDeleteSuccessDialog" max-width="520">
          <v-card>
            <v-card-title class="text-h6">Deletion scheduled</v-card-title>
            <v-card-text>
              <p>{{ deleteRequestSuccess }}</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="showDeleteSuccessDialog = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="showCancelDeleteDialog" max-width="520">
          <v-card>
            <v-card-title class="text-h6">Cancel deletion?</v-card-title>
            <v-card-text>
              <p>Do you want to cancel the scheduled deletion for this memory?</p>
              <v-alert v-if="cancelDeleteError" type="error" variant="tonal" class="mt-4">
                {{ cancelDeleteError }}
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="showCancelDeleteDialog = false" :disabled="cancelDeleteLoading">Cancel</v-btn>
              <v-btn
                color="success"
                :loading="cancelDeleteLoading"
                :disabled="cancelDeleteLoading"
                @click="cancelDeletionRequest"
              >
                Confirm cancel
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="showCancelDeleteSuccessDialog" max-width="520">
          <v-card>
            <v-card-title class="text-h6">Deletion canceled</v-card-title>
            <v-card-text>
              <v-alert type="success" variant="tonal">
                {{ cancelDeleteSuccess }}
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="showCancelDeleteSuccessDialog = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- RSVP Section (contacts only when there is no event to RSVP to) -->
        <v-card class="section-card mb-6" elevation="0">
          <v-card-title class="section-header primary-header">
            <v-icon left color="primary">mdi-email-check</v-icon>
            {{ hasEvent ? 'RSVPs' : 'Contacts' }}
          </v-card-title>
          <v-card-text class="pa-6">
            <!-- Contacts with their RSVPs -->
            <div v-if="contacts && contacts.length > 0">
              <h3 class="text-subtitle-1 font-weight-bold mb-3">Contacts</h3>
              <v-list>
                <v-list-item
                  v-for="(contact, index) in contacts"
                  :key="'contact-' + index"
                  class="rsvp-item mb-2"
                >
                  <template v-slot:prepend v-if="hasEvent">
                    <v-chip v-if="contact.rsvp?.status === 'ATTENDING'" color="success" size="small" variant="flat" class="mr-3 rsvp-status-chip rsvp-chip-attending">
                      <v-icon start size="16">mdi-check</v-icon>
                      Attending
                    </v-chip>
                    <v-chip v-else-if="contact.rsvp?.status === 'NOT_ATTENDING'" color="error" size="small" variant="tonal" class="mr-3 rsvp-status-chip rsvp-chip-dark">
                      <v-icon start size="16">mdi-close</v-icon>
                      Not Attending
                    </v-chip>
                    <v-chip v-else-if="contact.rsvp?.status === 'UNSURE'" color="grey-lighten-1" size="small" variant="flat" class="mr-3 rsvp-status-chip rsvp-chip-dark">
                      <v-icon start size="16">mdi-help</v-icon>
                      Not Sure
                    </v-chip>
                    <v-chip v-else color="grey-lighten-2" size="small" variant="flat" class="mr-3 rsvp-status-chip rsvp-chip-dark">
                      <v-icon start size="16">mdi-email-outline</v-icon>
                      No response
                    </v-chip>
                  </template>

                  <v-list-item-title>
                    {{ contact.email || contact.phone }}
                  </v-list-item-title>

                  <v-list-item-subtitle v-if="contact.rsvp?.message" class="mt-1">
                    "{{ contact.rsvp.message }}"
                  </v-list-item-subtitle>

                  <v-list-item-subtitle v-else-if="!contact.rsvp && !contact.dateInviteSent" class="mt-1">
                    No invite sent
                  </v-list-item-subtitle>

                  <template v-slot:append v-if="!contact.rsvp && contact.email">
                    <div class="d-flex align-center ga-2">
                      <v-btn
                        color="primary"
                        variant="outlined"
                        size="small"
                        @click="resendInvite(contact)"
                        :disabled="contact.sending || contact.sendStatus"
                      >
                        {{ contact.dateInviteSent ? 'Resend Invite' : 'Send Invite' }}
                      </v-btn>
                      <v-progress-circular
                        v-if="contact.sending"
                        indeterminate
                        size="20"
                        width="2"
                        color="primary"
                      ></v-progress-circular>
                      <v-icon
                        v-if="contact.sendStatus === 'success'"
                        color="success"
                        size="24"
                      >
                        mdi-check-circle
                      </v-icon>
                      <v-icon
                        v-if="contact.sendStatus === 'error'"
                        color="error"
                        size="24"
                      >
                        mdi-close-circle
                      </v-icon>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </div>

            <!-- Standalone RSVPs (no matching contact) -->
            <div v-if="hasEvent && standaloneRsvps && standaloneRsvps.length > 0" class="mt-6">
              <h3 v-if="contacts && contacts.length > 0" class="text-subtitle-1 font-weight-bold mb-3">Other Responses</h3>
              <v-list>
                <v-list-item
                  v-for="rsvp in standaloneRsvps"
                  :key="'standalone-' + rsvp.id"
                  class="rsvp-item mb-2"
                >
                  <template v-slot:prepend>
                    <v-chip v-if="rsvp.status === 'ATTENDING'" color="success" size="small" variant="flat" class="mr-3 rsvp-status-chip rsvp-chip-attending">
                      <v-icon start size="16">mdi-check</v-icon>
                      Attending
                    </v-chip>
                    <v-chip v-else-if="rsvp.status === 'NOT_ATTENDING'" color="error" size="small" variant="tonal" class="mr-3 rsvp-status-chip rsvp-chip-dark">
                      <v-icon start size="16">mdi-close</v-icon>
                      Not Attending
                    </v-chip>
                    <v-chip v-else color="grey-lighten-1" size="small" variant="flat" class="mr-3 rsvp-status-chip rsvp-chip-dark">
                      <v-icon start size="16">mdi-help</v-icon>
                      Not Sure
                    </v-chip>
                  </template>

                  <v-list-item-title>
                    {{ rsvp.name || rsvp.email || rsvp.phone }}
                    <span class="text-caption text-grey ml-2">· Via social link</span>
                  </v-list-item-title>

                  <v-list-item-subtitle v-if="rsvp.message" class="mt-1">
                    "{{ rsvp.message }}"
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </div>

            <!-- Empty state -->
            <div v-if="(!contacts || contacts.length === 0) && (!hasEvent || !standaloneRsvps || standaloneRsvps.length === 0)" class="text-center py-6 text--secondary">
              <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-email-outline</v-icon>
              <p>{{ hasEvent ? 'No contacts or RSVPs yet' : 'No contacts yet' }}</p>
            </div>
          </v-card-text>
        </v-card>

        <!-- Contributions Section -->
        <v-card class="section-card mb-6" elevation="0">
          <v-card-title class="section-header primary-header">
            <div class="d-flex align-center justify-space-between" style="width: 100%;">
              <div class="d-flex align-center">
                <v-icon left color="primary">mdi-heart-multiple</v-icon>
                Contributions
              </div>
              <v-chip
                v-if="unseenContributionsCount > 0"
                color="info"
                size="small"
                class="ml-2 white-text-chip"
                @click="scrollToFirstUnseen"
                style="cursor: pointer;"
              >
                <v-icon start size="16" color="white">mdi-new-box</v-icon>
                {{ unseenContributionsCount }} new
              </v-chip>
            </div>
          </v-card-title>
          <v-card-text class="pa-6">
            <div v-if="contributions && contributions.length > 0">
              <v-card
                v-for="contribution in contributions"
                :key="contribution.id"
                :ref="el => setContributionRef(el, contribution.id)"
                class="contribution-card mb-4"
                variant="outlined"
                :data-contribution-id="contribution.id"
                :class="{ 'unseen-contribution': !contribution.dateSeen, 'unapproved-contribution': !contribution.dateApproved }"
              >
                <!-- New Badge -->
                <v-chip
                  v-if="!contribution.dateSeen && !contribution.justSeen"
                  color="info"
                  size="small"
                  class="new-badge white-text-chip"
                  variant="flat"
                >
                  <v-icon start size="14" color="white">mdi-new-box</v-icon>
                  New
                </v-chip>

                <!-- Just Seen Badge (fades out) -->
                <v-chip
                  v-if="contribution.justSeen"
                  color="info"
                  size="small"
                  class="new-badge fade-out white-text-chip"
                  variant="flat"
                >
                  <v-icon start size="14" color="white">mdi-check</v-icon>
                  Seen
                </v-chip>

                <v-card-text>
                  <div
                    v-if="blockedContributionIds.has(contribution.id)"
                    class="blocked-placeholder"
                  >
                    <v-icon size="24" color="grey-darken-1" class="mr-2">mdi-block-helper</v-icon>
                    <span>Contribution blocked.</span>
                  </div>

                  <template v-else>
                    <div class="contribution-header mb-3">
                      <div>
                        <div class="font-weight-bold">{{ contribution.contributorName }}</div>
                        <div class="text-caption text--secondary">{{ formatDate(contribution.dateCreated) }}</div>
                      </div>
                      <v-spacer></v-spacer>
                      <div class="contribution-actions">
                        <v-btn
                          v-if="!contribution.dateApproved"
                          color="success"
                          size="small"
                          :loading="approvingContributionIds.has(contribution.id)"
                          :disabled="approvingContributionIds.has(contribution.id) || blockingContributionIds.has(contribution.id)"
                          @click="approveContribution(contribution)"
                        >
                          Approve
                        </v-btn>
                        <v-btn
                          color="warning"
                          size="small"
                          :loading="blockingContributionIds.has(contribution.id)"
                          :disabled="blockingContributionIds.has(contribution.id) || approvingContributionIds.has(contribution.id)"
                          @click="blockContribution(contribution)"
                        >
                          Block
                        </v-btn>
                        <span v-if="contribution.dateApproved" class="approval-status text-caption">
                          Approved
                        </span>
                      </div>
                    </div>

                    <div v-if="contribution.message" class="mb-3">
                      <p class="text-body-1">{{ contribution.message }}</p>
                    </div>

                    <div v-if="contribution.media && contribution.media.length > 0" class="contribution-media">
                      <div
                        v-for="media in contribution.media"
                        :key="media.uuid"
                        class="media-item mb-3 d-flex align-start"
                      >
                        <AudioEntry v-if="media.mediaType === 4" :media="media" :memorial-slug="memorial.shareSlug" class="flex-grow-1" />
                        <template v-else>
                        <v-img
                          :src="getMediaUrl(media)"
                          :alt="media.title || 'Contribution photo'"
                          aspect-ratio="1"
                          cover
                          class="rounded mr-3"
                          width="150"
                          max-width="150"
                          min-width="150"
                        ></v-img>
                        <div v-if="media.title" class="text-body-2 flex-grow-1">
                          {{ media.title }}
                        </div>
                        </template>
                      </div>
                    </div>
                  </template>
                </v-card-text>
              </v-card>
            </div>

            <div v-else class="text-center py-6 text--secondary">
              <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-heart-outline</v-icon>
              <p>No contributions yet</p>
            </div>
          </v-card-text>
        </v-card>

        <!-- Donations Section -->
        <v-card v-if="memorialFunds && memorialFunds.length > 0" class="section-card mb-6" elevation="0">
          <v-card-title class="section-header primary-header">
            <v-icon left color="primary">mdi-currency-usd</v-icon>
            Donations
          </v-card-title>
          <v-card-text class="pa-6">
            <div v-for="fund in memorialFunds" :key="fund.id" class="mb-6">
              <h3 class="text-h6 mb-2">{{ fund.title }}</h3>
              <p v-if="fund.description" class="text-body-2 text--secondary mb-3">{{ fund.description }}</p>

              <div v-if="fund.donations && fund.donations.length > 0">
                <v-list density="compact">
                  <v-list-item
                    v-for="donation in fund.donations"
                    :key="donation.id"
                    class="mb-2"
                  >
                    <v-list-item-title>
                      <span class="font-weight-bold">${{ donation.amount }}</span>
                      <span class="ml-2 text-caption font-weight-light text--secondary">{{ formatDate(donation.dateDonated) }}</span>
                      <span class="ml-2" v-if="donation.anon">Anonymous</span>
                      <span class="ml-2" v-else>{{ donation.user?.firstName }} {{ donation.user?.surname }}</span>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </div>

              <div v-else class="text-center py-4 text--secondary">
                <p class="text-body-2">No donations yet for this fund</p>
              </div>
            </div>
          </v-card-text>
        </v-card>
        </div>
      </div>
    </template>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog.show" max-width="500">
      <v-card>
        <v-card-title class="bg-error text-white d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
          Error
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1">{{ errorDialog.message }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="elevated"
            @click="errorDialog.show = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import authService from '../services/auth'
import ProfileMenu from '../components/ProfileMenu.vue'
import AudioEntry from '../components/media/AudioEntry.vue'
import { formatLifeDate } from '../utils/helpers'

const router = useRouter()
const route = useRoute()
const store = useMemorialStore()

const loading = ref(true)
const error = ref('')
const memorial = ref(null)
const contributions = ref([])
const memorialFunds = ref([])
const mapExpanded = ref(false)
const showDeleteDialog = ref(false)
const showDeleteSuccessDialog = ref(false)
const deleteRequestLoading = ref(false)
const deleteRequestError = ref('')
const deleteRequestSuccess = ref('')
const showCancelDeleteDialog = ref(false)
const showCancelDeleteSuccessDialog = ref(false)
const cancelDeleteLoading = ref(false)
const cancelDeleteError = ref('')
const cancelDeleteSuccess = ref('')
const approvingContributionIds = ref(new Set())
const blockingContributionIds = ref(new Set())
const blockedContributionIds = ref(new Set())

// Computed property for unseen contributions count
const unseenContributionsCount = computed(() => {
  return contributions.value.filter(c => !c.dateSeen && !c.justSeen).length
})

// Memorials created before events became optional have no flag and always had one
const hasEvent = computed(() => {
  return memorial.value?.hasEvent !== false
})

const contacts = ref([])
const standaloneRsvps = ref([])

// Editable memorial details
const editableNameOfPerson = ref('')
const editableMemorialDate = ref('')
const editableAddress = ref('')
const editableDetails = ref('')
const detailsChanged = ref(false)
const generatingPdf = ref(false)

// Error dialog
const errorDialog = ref({
  show: false,
  message: ''
})

const showError = (message) => {
  errorDialog.value.message = message
  errorDialog.value.show = true
}

const approveContribution = async (contribution) => {
  if (!store.apiToken) return
  approvingContributionIds.value.add(contribution.id)
  try {
    const response = await apiService.approveContribution(contribution.id, true, store.apiToken)
    if (response.success) {
      contribution.dateApproved = new Date().getTime()
    } else {
      showError(response.msg || 'Unable to approve contribution.')
    }
  } catch (err) {
    console.error('Approve contribution error:', err)
    showError('Unable to approve contribution. Please try again.')
  } finally {
    approvingContributionIds.value.delete(contribution.id)
  }
}

const blockContribution = async (contribution) => {
  if (!store.apiToken) return
  blockingContributionIds.value.add(contribution.id)
  try {
    const response = await apiService.blockContribution(contribution.id, store.apiToken)
    if (response.success) {
      blockedContributionIds.value.add(contribution.id)
    } else {
      showError(response.msg || 'Unable to block contribution.')
    }
  } catch (err) {
    console.error('Block contribution error:', err)
    showError('Unable to block contribution. Please try again.')
  } finally {
    blockingContributionIds.value.delete(contribution.id)
  }
}

const loadDashboardData = async () => {
  loading.value = true
  error.value = ''

  try {
    // Check if user is authenticated
    if (!store.apiToken) {
      authService.logout()
      return
    }

    // Check for UUID query parameter to clear cache and reload
    if (route.query.uuid) {
      console.log('UUID query parameter detected, clearing cache and reloading memorial:', route.query.uuid)
      // Clear the memorial cache by updating store with new UUID
      store.memorialUuid = route.query.uuid
    }

    if (!store.memorialUuid) {
      error.value = 'No memory found. Please create or select a memory.'
      return
    }

    // Load memorial data
    const memorialResponse = await apiService.getMemorial(store.memorialUuid)
    if (memorialResponse.success && memorialResponse.memorial) {
      memorial.value = memorialResponse.memorial

      // Initialize editable fields
      editableNameOfPerson.value = memorial.value.nameOfPerson || ''
      // memorialDate is already in YYYY-MM-DD format from the backend
      editableMemorialDate.value = memorial.value.memorialDate || ''
      editableAddress.value = memorial.value.address || ''
      editableDetails.value = memorial.value.details || ''
      detailsChanged.value = false

      // Extract RSVPs and contacts from memorial data
      const allRsvps = memorialResponse.memorial.rsvps || []
      const allContacts = memorialResponse.memorial.contacts || []

      // Create a map of RSVPs by email/phone for quick lookup
      const rsvpMap = new Map()
      allRsvps.forEach(rsvp => {
        const key = rsvp.email || rsvp.phone
        if (key) {
          rsvpMap.set(key.toLowerCase(), rsvp)
        }
      })

      // Match contacts with their RSVPs
      contacts.value = allContacts.map(contact => {
        const contactKey = (contact.email || contact.phone || '').toLowerCase()
        const matchedRsvp = rsvpMap.get(contactKey)

        if (matchedRsvp) {
          // Remove from map so we can track unmatched RSVPs
          rsvpMap.delete(contactKey)
        }

        return {
          ...contact,
          rsvp: matchedRsvp || null
        }
      })

      // Remaining RSVPs without matching contacts (e.g., from social media links)
      standaloneRsvps.value = Array.from(rsvpMap.values())

      // Sort contributions by dateCreated descending (latest first)
      contributions.value = (memorialResponse.memorial.contributions || []).sort((a, b) => {
        return new Date(b.dateCreated) - new Date(a.dateCreated)
      })

      // Load memorial funds
      memorialFunds.value = memorialResponse.memorial.memorialFunds || []
    } else {
      // Memorial not found or no permission - log out
      console.error('Memorial not found or no permission:', memorialResponse.msg)
      authService.logout()
    }
  } catch (err) {
    console.error('Dashboard load error:', err)
    // Check if it's a permission error (403) or not found (404)
    if (err.response && (err.response.status === 403 || err.response.status === 404)) {
      console.error('Permission denied or memorial not found, logging out')
      authService.logout()
    } else {
      error.value = 'Unable to load dashboard. Please try again.'
      loading.value = false
    }
  } finally {
    // Only set loading to false if we haven't logged out
    if (store.apiToken) {
      loading.value = false
    }
  }
}

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatMemorialDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const mapSrc = computed(() => {
  if (!memorial.value?.address || !memorial.value.address.trim()) return ''
  return `https://www.google.com/maps?q=${encodeURIComponent(memorial.value.address.trim())}&output=embed`
})

const getMediaUrl = (media) => {
  if (media.location) {
    return `${media.location}/${media.filename}.${media.extension}`
  }
  return `https://assets.forever-x.com/photos/${media.filename}.${media.extension}`
}

const toggleMap = () => {
  mapExpanded.value = !mapExpanded.value
}

const editDetails = () => {
  router.push('/memory/edit-details')
}

const editMessage = () => {
  router.push('/memory/edit-message')
}

const editPhotos = () => {
  router.push('/memory/edit-photos')
}

const editStories = () => {
  router.push('/memory/edit-stories')
}

const editContactsAndShare = () => {
  router.push('/memory/edit-contacts-share')
}

const editAdministrators = () => {
  router.push('/memory/administrators')
}

const viewFeed = () => {
  router.push('/memory/feed')
}

const viewSlideShow = () => {
  router.push('/memory/slideshow')
}

const viewVideoSlideShow = () => {
  router.push('/memory/video-slideshow')
}

const requestDeletion = async () => {
  if (!store.apiToken || !store.memorialUuid) return
  deleteRequestLoading.value = true
  deleteRequestError.value = ''
  deleteRequestSuccess.value = ''

  try {
    const response = await apiService.requestMemorialDeletion(store.memorialUuid, store.apiToken)
    if (response.success) {
      deleteRequestSuccess.value = response.msg || 'Deletion requested. Your memorial is scheduled for deletion in 7 days.'
      showDeleteDialog.value = false
      showDeleteSuccessDialog.value = true
      if (memorial.value) {
        memorial.value.dateDeleteRequested = Date.now()
      }
    } else {
      deleteRequestError.value = response.msg || 'Unable to request deletion.'
    }
  } catch (err) {
    console.error('Request deletion error:', err)
    deleteRequestError.value = 'Unable to request deletion. Please try again.'
  } finally {
    deleteRequestLoading.value = false
  }
}

const cancelDeletionRequest = async () => {
  if (!store.apiToken || !store.memorialUuid) return
  cancelDeleteLoading.value = true
  cancelDeleteError.value = ''
  cancelDeleteSuccess.value = ''

  try {
    const response = await apiService.cancelMemorialDeletion(store.memorialUuid, store.apiToken)
    if (response.success) {
      cancelDeleteSuccess.value = response.msg || 'Deletion request canceled.'
      showCancelDeleteDialog.value = false
      showCancelDeleteSuccessDialog.value = true
      if (memorial.value) {
        memorial.value.dateDeleteRequested = null
      }
    } else {
      cancelDeleteError.value = response.msg || 'Unable to cancel deletion request.'
    }
  } catch (err) {
    console.error('Cancel deletion error:', err)
    cancelDeleteError.value = 'Unable to cancel deletion request. Please try again.'
  } finally {
    cancelDeleteLoading.value = false
  }
}


const exportToPdf = async () => {
  generatingPdf.value = true

  try {
    const response = await apiService.generatePdf(store.memorialUuid, store.apiToken)

    if (response.success) {
      // Update memorial with new PDF URL
      memorial.value.pdfUrl = response.pdfUrl
      memorial.value.pdfGeneratedAt = new Date().getTime()
      console.log('PDF generated successfully:', response.pdfUrl)
    } else {
      console.error('Failed to generate PDF:', response.msg)
      showError('Failed to generate PDF: ' + (response.msg || 'Unknown error'))
    }
  } catch (err) {
    console.error('Error generating PDF:', err)
    showError('Error generating PDF. Please try again.')
  } finally {
    generatingPdf.value = false
  }
}

const resendInvite = async (contact) => {
  if (!contact.uuid) {
    console.error('Contact UUID missing')
    return
  }

  // Set sending state
  contact.sending = true
  contact.sendStatus = null

  try {
    const response = await apiService.post('/memory/resend-invite', {
      contactUuid: contact.uuid,
      memorialUuid: store.memorialUuid
    })

    if (response.success) {
      console.log('Invite sent successfully to', contact.email)
      contact.sendStatus = 'success'
      // Update dateInviteSent to current date so it shows "Resend Invite"
      contact.dateInviteSent = Date.now()

      // Clear success icon after 2 seconds
      setTimeout(() => {
        contact.sendStatus = null
      }, 2000)
    } else {
      console.error('Failed to send invite:', response.msg)
      contact.sendStatus = 'error'

      // Clear error icon after 2 seconds
      setTimeout(() => {
        contact.sendStatus = null
      }, 2000)
    }
  } catch (err) {
    console.error('Error sending invite:', err)
    contact.sendStatus = 'error'

    // Clear error icon after 2 seconds
    setTimeout(() => {
      contact.sendStatus = null
    }, 2000)
  } finally {
    contact.sending = false
  }
}

// Contribution visibility tracking
const contributionRefs = new Map()
let observer = null

const setContributionRef = (el, contributionId) => {
  if (el) {
    // Get the actual DOM element - el might be a Vue component instance with $el
    const element = el.$el || el
    contributionRefs.set(contributionId, element)
  } else {
    contributionRefs.delete(contributionId)
  }
}

const markContributionSeen = async (contributionId) => {
  try {
    const contribution = contributions.value.find(c => c.id === contributionId)
    if (!contribution || contribution.dateSeen || contribution.justSeen) {
      return // Already seen or not found
    }

    const response = await apiService.markContributionSeen(contributionId, store.apiToken)
    if (response.success) {
      // Update the local contribution object
      contribution.dateSeen = response.contribution.dateSeen
      contribution.justSeen = true
      console.log('Marked contribution as seen:', contributionId)

      // Hide the "Seen" badge after 3 seconds
      setTimeout(() => {
        contribution.justSeen = false
      }, 3000)
    }
  } catch (err) {
    console.error('Error marking contribution as seen:', err)
  }
}

const scrollToFirstUnseen = () => {
  const firstUnseen = contributions.value.find(c => !c.dateSeen && !c.justSeen)
  if (firstUnseen) {
    const element = contributionRefs.get(firstUnseen.id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }
}

const setupContributionObserver = () => {
  // Clean up existing observer
  if (observer) {
    observer.disconnect()
  }

  // Create intersection observer
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const contributionId = parseInt(entry.target.dataset.contributionId)
          if (contributionId) {
            markContributionSeen(contributionId)
          }
        }
      })
    },
    {
      threshold: 0.5 // Consider visible when 50% of element is in viewport
    }
  )

  // Observe all contribution elements
  contributionRefs.forEach((element) => {
    if (element && element instanceof Element) {
      observer.observe(element)
    }
  })
}

onMounted(async () => {
  await loadDashboardData()

  // Setup observer after contributions are loaded
  await nextTick()
  setupContributionObserver()
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
.memorial-dashboard-page {
  min-height: 100vh;
}

/* Band layout */
.band {
  width: 100%;
}

.band-content {
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem 1.5rem;
}

.band-hero {
  background: var(--v-theme-background);
}

.band-details {
  background: #1a1a1a;
  color: #e0e0e0;
}

.band-red-stripe {
  width: 100%;
  height: 30px;
  background: #D4401A;
  flex-shrink: 0;
}

.band-main {
  background: #ffffff;
  padding-top: 1.5rem;
  padding-bottom: 2rem;
}

.band-main .band-content {
  padding-top: 0;
}

.page-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* symmetric gutters reserve room for the profile menu pinned to the right, so
     a long memorial name truncates before it can run underneath the icon */
  padding-left: 64px;
  padding-right: 64px;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

/* The router-link wrapper has to be shrinkable too, otherwise the name never
   truncates; flex also removes the inline baseline gap under the brand text
   that was pushing the profile icon out of alignment with the title. */
.brand-header > a {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.brand-logo {
  height: 40px;
  width: auto;
}

.brand-title {
  letter-spacing: 0.1rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0;
}


.page-title {
  font-weight: 600;
  color: var(--v-theme-black);
}

.section-card {
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: 0 24px 45px -20px rgba(15, 23, 42, 0.2);
  background: var(--v-theme-surface);
}

.section-header {
  font-weight: 600 !important;
  border-bottom: 1px solid var(--v-theme-border);
}

.primary-header {
  background: white !important;
  color: rgb(var(--v-theme-primary)) !important;
  border-bottom: 1px solid var(--v-theme-border);
}

/* Memorial Hero Section */
.memorial-hero {
  padding: 2rem 0;
}

.hero-layout {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 2rem;
}

.hero-photo-container {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.hero-photo {
  width: 300px;
  height: 300px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.hero-photo-placeholder {
  width: 300px;
  height: 300px;
  background: var(--v-theme-gray100);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.hero-name {
  font-family: var(--v-theme-headingFont);
  font-size: 2.5rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0 0 0.6rem 0;
}

.hero-info {
  text-align: left;
  max-width: 520px;
  width: 100%;
  align-self: flex-start;
}

.remembering-title {
  font-size: 0.95rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--v-theme-gray500);
  margin-bottom: 0.35rem;
}

.hero-details-panel {
  width: 100%;
  max-width: 520px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.life-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.75rem;
  margin: 0 0 0.35rem;
  color: #ffffff;
  font-family: var(--v-theme-headingFont);
  font-size: 2rem;
  font-weight: 400;
  line-height: 1.2;
}

.life-heading span {
  color: #e0e0e0;
  font-family: inherit;
  font-size: 1.15rem;
  white-space: nowrap;
}

.hero-detail-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: #e0e0e0;
  font-size: 1rem;
}

.hero-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.address-detail {
  flex: 1;
  justify-content: flex-start;
}

.map-mini-icon {
  width: 64px;
  height: 64px;
  display: block;
  cursor: pointer;
  flex-shrink: 0;
  object-fit: contain;
  opacity: 0.8;
}

.hero-details-text {
  margin-top: 0.15rem;
  text-align: left;
  line-height: 1.5;
  color: #bdbdbd;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rsvp-item {
  border: 1px solid var(--v-theme-border);
  border-radius: 12px;
  padding: 8px;
  margin-bottom: 8px;
}

.rsvp-item :deep(.v-list-item__prepend) {
  align-self: flex-start;
  padding-top: 4px;
}

.rsvp-status-chip {
  width: 140px;
  justify-content: center;
}

:deep(.rsvp-chip-attending .v-chip__content),
:deep(.rsvp-chip-attending .v-icon) {
  color: #ffffff !important;
}

:deep(.rsvp-chip-dark .v-chip__content),
:deep(.rsvp-chip-dark .v-icon) {
  color: rgba(0, 0, 0, 0.87) !important;
}


.contribution-card {
  border-radius: 12px;
  position: relative;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.unseen-contribution {
  border-color: rgb(var(--v-theme-info)) !important;
  border-width: 2px !important;
  box-shadow: 0 2px 8px rgba(146, 214, 227, 0.15);
}

.unapproved-contribution {
  border-color: rgba(251, 146, 60, 0.4) !important;
}

.blocked-placeholder {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px;
  background: var(--v-theme-background);
  border-radius: 12px;
  color: var(--v-theme-gray500);
}

.contribution-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.contribution-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.approval-status {
  color: var(--v-theme-gray500);
}

.new-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  font-weight: 600;
}

.fade-out {
  animation: fadeOut 3s forwards;
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  70% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.white-text-chip {
  color: white !important;
}

.contribution-media {
  display: grid;
  gap: 16px;
}

.media-item {
  max-width: 100%;
}

@media (max-width: 600px) {
  .action-buttons {
    flex-direction: column;
  }

  .action-buttons .v-btn {
    width: 100%;
  }

  .hero-photo,
  .hero-photo-placeholder {
    width: 220px;
    height: 220px;
  }

  .hero-layout {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .hero-info {
    text-align: center;
  }

  .hero-details-panel {
    margin-left: auto;
    margin-right: auto;
  }

  .hero-detail-item {
    justify-content: center;
  }

  .hero-detail-row {
    justify-content: center;
  }

  .hero-name {
    font-size: 1.75rem;
  }

  .hero-detail-item {
    font-size: 0.9rem;
  }

  /* Stack RSVP rows vertically so the email address is never squeezed out */
  .rsvp-item {
    grid-template-areas: "prepend" "content" "append";
    grid-template-columns: 1fr;
  }

  .rsvp-item :deep(.v-list-item__prepend) {
    padding-top: 0;
    margin-bottom: 8px;
  }

  .rsvp-item :deep(.v-list-item__append) {
    margin-top: 8px;
  }

  .rsvp-item :deep(.v-list-item-title) {
    white-space: normal;
    word-break: break-word;
  }
}
</style>
